import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Smartphone, ShieldCheck, Share, PlusSquare } from "lucide-react";
import { WizardProgress, WizardFooter, Button, Card, CardBody, Field, Input, Pill } from "@/design/components";
import { ImpactLedgerLogo } from "@/shell/ImpactLedgerLogo";
import { useStartOnboarding, useCompleteStep, useCompleteOnboarding } from "@/domain/useOnboarding";
import type { OnboardingPeek } from "@/domain/api";

// Tier-4 reporter onboarding — spec §9.4: "Three lightweight screens: name
// confirmation, PWA install prompt with friendly explanation, device pairing.
// No approval gate by default. Lands on the Reporter inbox (Section 7)."
//
// Only screen 1 commits to the backend (confirm_profile). The PWA-install and
// device-pairing screens are UI-only; the router auto-skips the catalogue's
// profile_photo_optional / webauthn_optional steps (there is no photo or WebAuthn
// step in the spec). Finish -> completeSession -> /reporter.
const STEPS = [
  { key: "name", label: "Your name" },
  { key: "install", label: "Add to phone" },
  { key: "pair", label: "Pair device" },
];

// Minimal shape of the beforeinstallprompt event (not in TS's lib DOM types).
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function splitName(full: string | undefined): { firstname: string; lastname: string } {
  if (!full || !full.trim()) return { firstname: "", lastname: "" };
  const parts = full.trim().split(/\s+/);
  return { firstname: parts[0], lastname: parts.slice(1).join(" ") };
}

export function ReporterWizard({ token, peek }: { token: string | undefined; peek: OnboardingPeek }) {
  const navigate = useNavigate();
  const { start, isPending: starting } = useStartOnboarding();
  const { completeStep, isPending: completingStep } = useCompleteStep();
  const { completeOnboarding, isPending: finalizing } = useCompleteOnboarding();

  const seededName = splitName((peek.invite.payload?.inviteeName as string) || undefined);
  const [step, setStep] = useState(0);
  const [firstname, setFirstname] = useState(seededName.firstname);
  const [lastname, setLastname] = useState(seededName.lastname);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [blocker, setBlocker] = useState<string | null>(null);

  // Capture the browser's install prompt if it fires, so the install screen can
  // offer a real "Add to home screen" button on supported devices (Android
  // Chrome). Elsewhere (e.g. iOS Safari) we show manual instructions.
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const busy = starting || completingStep || finalizing;

  // The invite may already be used (e.g. reopened after starting) — reporter
  // onboarding has no cross-session resume yet, so guide them rather than error.
  if (peek.state !== "active" && sessionId == null) {
    const body =
      peek.state === "consumed"
        ? "This invitation has already been opened. If it was you, continue on the device where you started; otherwise ask your team lead for a new link."
        : "This invitation is no longer valid. Ask your team lead to send a new one.";
    return <ReporterSplash icon={<ShieldCheck className="h-6 w-6" />} title="This invite can't be opened" body={body} />;
  }

  async function onContinue() {
    if (busy) return;
    setBlocker(null);

    // Screen 1 — name confirmation (the only backend commit).
    if (step === 0) {
      if (!firstname.trim() || !lastname.trim()) {
        setBlocker("Add your first and last name before continuing.");
        return;
      }
      let sid = sessionId;
      if (!sid) {
        const res = await start(token as string, {});
        if (!res) {
          setBlocker("We couldn't start your setup. Please try again.");
          return;
        }
        sid = res.session.id;
        setSessionId(sid);
      }
      const session = await completeStep(sid, "confirm_profile", {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
      });
      if (!session) {
        setBlocker("We couldn't save your name. Please try again.");
        return;
      }
      setStep(1);
      return;
    }

    // Screen 2 — PWA install prompt (UI only).
    if (step === 1) {
      setStep(2);
    }
  }

  async function triggerInstall() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    setInstallPrompt(null);
  }

  // Screen 3 — device pairing → finish onboarding.
  async function onFinish() {
    if (busy || sessionId == null) return;
    const result = await completeOnboarding(sessionId);
    if (!result) {
      setBlocker("We couldn't finish your setup. Please try again.");
      return;
    }
    navigate(result.landing || "/reporter");
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="flex h-16 items-center justify-between border-b border-line bg-card px-6">
        <ImpactLedgerLogo />
        <div className="flex items-center gap-3">
          <span className="text-sm text-mute tabular-nums">Step <span className="font-medium text-ink">{step + 1}</span> of {STEPS.length}</span>
          <Pill variant="neutral">Reporter setup</Pill>
        </div>
      </header>

      <div className="border-b border-line bg-card px-6 py-3">
        <WizardProgress steps={STEPS} currentIndex={step} />
      </div>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-xl px-6 py-8">
          {step === 0 && (
            <div>
              <StepHead title="Confirm your name" body="You have been invited as a field reporter. Confirm your name so your spend asks and evidence are attributed to you." />
              <Card>
                <CardBody className="space-y-4">
                  <div className="rounded-md bg-subtle px-3 py-2 text-xs text-mute">
                    Invitation sent to <span className="font-medium text-ink">{peek.invite.email}</span>.
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="First name" required>
                      <Input value={firstname} onChange={(e) => setFirstname(e.target.value)} />
                    </Field>
                    <Field label="Last name" required>
                      <Input value={lastname} onChange={(e) => setLastname(e.target.value)} />
                    </Field>
                  </div>
                </CardBody>
              </Card>
            </div>
          )}

          {step === 1 && (
            <div>
              <StepHead title="Add Impact Ledger to your phone" body="Add the app to your home screen so you can open it with one tap in the field — it works on low-bandwidth connections and remembers you." />
              <Card>
                <CardBody className="space-y-4">
                  <div className="flex items-center gap-3 rounded-md bg-brandSoft px-4 py-3 text-sm text-brand">
                    <Smartphone className="h-5 w-5 shrink-0" />
                    <span>Once added, Impact Ledger opens like any other app — no browser, no typing a web address.</span>
                  </div>
                  {installPrompt ? (
                    <Button onClick={triggerInstall}>
                      <PlusSquare className="h-4 w-4" /> Add to home screen
                    </Button>
                  ) : installed ? (
                    <p className="flex items-center gap-2 text-sm text-ok"><Check className="h-4 w-4" /> Added to your home screen.</p>
                  ) : (
                    <div className="rounded-md border border-line p-3 text-sm text-mute">
                      <p className="font-medium text-ink">To add it manually:</p>
                      <ol className="mt-1 list-decimal space-y-1 pl-5">
                        <li>Open your browser menu <Share className="inline h-3.5 w-3.5" /> (or the ⋮ menu).</li>
                        <li>Choose <span className="font-medium text-ink">Add to Home screen</span>.</li>
                      </ol>
                      <p className="mt-2 text-xs text-faint">You can also do this later — tap Continue to finish setting up.</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          )}

          {step === 2 && (
            <div>
              <StepHead title="Pair this device" body="This phone is now linked to your reporter account." />
              <Card>
                <CardBody className="flex flex-col items-center gap-4 py-8 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-okSoft text-ok"><Check className="h-8 w-8" /></span>
                  <div>
                    <p className="text-base font-medium text-ink">You're all set, {firstname || "reporter"}.</p>
                    <p className="mt-1 text-sm text-mute">You'll stay signed in on this device, so you can raise spend asks and submit evidence quickly — even on a patchy connection.</p>
                  </div>
                  {blocker && <p role="alert" className="text-sm text-bad">{blocker}</p>}
                  <Button className="mt-2" onClick={onFinish} disabled={busy}>{busy ? "Finishing…" : "Go to my inbox"}</Button>
                </CardBody>
              </Card>
            </div>
          )}
        </div>
      </main>

      {step < 2 && (
        <div className="border-t border-line bg-card">
          {blocker && (
            <div className="mx-auto max-w-xl px-6 pt-3">
              <p role="alert" className="rounded-md border border-warn/30 bg-warnSoft px-3 py-2 text-sm text-warn">{blocker}</p>
            </div>
          )}
          <WizardFooter
            className="border-t-0"
            showBack={step > 0}
            onBack={() => { setBlocker(null); setStep((s) => Math.max(0, s - 1)); }}
            onContinue={onContinue}
            continueDisabled={busy}
            continueLabel={busy ? "Working…" : "Continue"}
          />
        </div>
      )}
    </div>
  );
}

function StepHead({ title, body }: { title: string; body: string }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
      <p className="mt-1 text-sm text-mute">{body}</p>
    </div>
  );
}

function ReporterSplash({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="flex h-16 items-center border-b border-line bg-card px-6">
        <ImpactLedgerLogo />
      </header>
      <main className="flex flex-1 items-center justify-center px-6">
        <Card className="w-full max-w-md">
          <CardBody className="space-y-4 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brandSoft text-brand">{icon}</span>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
              <p className="mt-1 text-sm text-mute">{body}</p>
            </div>
          </CardBody>
        </Card>
      </main>
    </div>
  );
}
